import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BankAccount } from '../models/bank-account';
import { ItemGroup, CostItem } from '../models/cost-item';
import { InvoiceData, InvoicePostRequest } from '../models/invoice';
import { Versioning } from '../models/version';
import { CommunicationManagerService } from './communication-manager.service';
import { CostItemService } from './cost-item.service';
import { ItemGroupService } from './item-group.service';
import { ProjectManagerService } from './project-manager.service';

@Injectable({ providedIn: 'root' })
export class ProjectExpensesService {
  constructor(
    private costItemService: CostItemService,
    private itemGroupService: ItemGroupService,
    private projectManagerService: ProjectManagerService,
    private communicationManagerService: CommunicationManagerService
  ) {}

  getOrCreateProjectCost(projectId: string, companyId: string) {
    return this.projectManagerService.getOrCreateProjectCost(
      projectId,
      companyId
    ) as Observable<string>;
  }

  getItemGroups(projectId: string) {
    return this.itemGroupService.getAllProjectItemGroups(
      projectId
    ) as Observable<ItemGroup[]>;
  }

  getCostItems(projectId: string) {
    return this.costItemService.getCostItemsByProject(projectId) as Observable<
      CostItem[]
    >;
  }

  getInvoices(versionLink: string) {
    return this.communicationManagerService.get(
      `invoice/all/${versionLink}`
    ) as Observable<InvoiceData[]>;
  }

  updateInvoice(invoice: InvoiceData) {
    invoice.status =
      invoice.receivedAmount === 0
        ? 'Sent'
        : invoice.receivedAmount < invoice.invoiceAmount
        ? 'Partial'
        : 'Paid';
    return this.communicationManagerService.put(
      `invoice/${invoice._id}`,
      {
        receivedAmount: invoice.receivedAmount,
        paymentReceivedDate: new Date(
          invoice.paymentReceivedDate
        ).toLocaleDateString('en-US') as any,
        status: invoice.status,
      } as InvoiceData,
      [{ headerName: 'projectId', headerValue: invoice.project }]
    ) as Observable<InvoiceData>;
  }

  createInvoice(projectId: string, invoice: InvoicePostRequest) {
    return this.communicationManagerService.postAndGetJson(`invoice`, invoice, [
      { headerName: 'projectId', headerValue: projectId },
    ]) as Observable<InvoiceData>;
  }

  deleteInvoice(projectId: string, invoiceId: string) {
    return this.communicationManagerService.delete(`invoice/${invoiceId}`, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  getVersioning(projectId: string) {
    return this.communicationManagerService.get('project/versioning', [
      { headerName: 'projectId', headerValue: projectId },
    ]) as Observable<Versioning>;
  }

  getBankAccounts() {
    return this.communicationManagerService.get('bankAccount') as Observable<
      BankAccount[]
    >;
  }
}
